
import React, { useState } from 'react';
import GameEditDialog from '@/components/admin/GameEditDialog';
import FakePlayCountDialog from '@/components/admin/FakePlayCountDialog';
import BulkActionProgressDialog from '@/components/admin/games/BulkActionProgressDialog';
import AdminGamesHeader from '@/components/admin/games/AdminGamesHeader';
import AdminGamesFilters from '@/components/admin/games/AdminGamesFilters';
import AdminGamesBulkActions from '@/components/admin/games/AdminGamesBulkActions';
import AdminGameCard from '@/components/admin/games/AdminGameCard';
import AdminGamesPagination from '@/components/admin/games/AdminGamesPagination';
import AdminGamesSelectHeader from '@/components/admin/games/AdminGamesSelectHeader';
import AdminGamesEmptyState from '@/components/admin/games/AdminGamesEmptyState';
import AdminGamesLoadingState from '@/components/admin/games/AdminGamesLoadingState';
import AdminGamesTools from '@/components/admin/games/AdminGamesTools';
import { useAdminGames } from '@/hooks/useAdminGames';
import { useAdminGamesBulkActions } from '@/hooks/useAdminGamesBulkActions';
import { useAdminGamesHandlers } from '@/components/admin/games/AdminGamesHandlers';

const AdminGames = () => {
  const [editingGame, setEditingGame] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [fakePlayCountDialogOpen, setFakePlayCountDialogOpen] = useState(false);
  const [editingPlayCountGame, setEditingPlayCountGame] = useState<any>(null);
  const [showTools, setShowTools] = useState(false);

  const {
    searchTerm,
    setSearchTerm,
    categoryFilter,
    setCategoryFilter,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    games,
    totalGames,
    pageCount,
    categories,
    isLoading,
    selectAllUnconfiguredGames,
    selectAllUnconfiguredPlayCount,
    selectAllUnconfiguredRatings
  } = useAdminGames();

  const {
    selectedGames,
    setSelectedGames,
    bulkAction,
    setBulkAction,
    bulkCategory,
    setBulkCategory,
    bulkFakePlayCount,
    setBulkFakePlayCount,
    bulkFakePlayMin,
    setBulkFakePlayMin,
    bulkFakePlayMax,
    setBulkFakePlayMax,
    bulkRatingsCountMin,
    setBulkRatingsCountMin,
    bulkRatingsCountMax,
    setBulkRatingsCountMax,
    useFakePlayRange,
    setUseFakePlayRange,
    clearBulkSelection,
    progressDialogOpen,
    setProgressDialogOpen,
    progressData,
    startProgress,
    updateProgress,
    completeProgress,
    getFailedGameIds
  } = useAdminGamesBulkActions();

  const {
    handleSelectAll,
    handleSelectAllPages,
    handleSelectGame,
    handleBulkAction,
    handleToggleVisibility,
    handleDeleteGame,
    isLoading: isActionLoading
  } = useAdminGamesHandlers({
    selectedGames,
    bulkAction,
    bulkCategory,
    bulkFakePlayCount,
    bulkFakePlayMin,
    bulkFakePlayMax,
    bulkRatingsCountMin,
    bulkRatingsCountMax,
    useFakePlayRange,
    onClearBulkSelection: clearBulkSelection,
    onStartProgress: startProgress,
    onUpdateProgress: updateProgress,
    onCompleteProgress: completeProgress
  });

  // Handle edit game
  const handleEditGame = (game: any) => {
    setEditingGame(game);
    setEditDialogOpen(true);
  };

  // Handle add new game
  const handleAddNewGame = () => {
    setEditingGame(null);
    setEditDialogOpen(true);
  };

  // Handle edit fake play count
  const handleEditFakePlayCount = (game: any) => {
    setEditingPlayCountGame(game);
    setFakePlayCountDialogOpen(true);
  };

  // Handle selecting all unconfigured games
  const handleSelectAllUnconfigured = async () => {
    const unconfiguredGameIds = await selectAllUnconfiguredGames();
    setSelectedGames(unconfiguredGameIds);
  };

  // Handle selecting games with missing play count
  const handleSelectAllUnconfiguredPlayCount = async () => {
    const unconfiguredGameIds = await selectAllUnconfiguredPlayCount();
    setSelectedGames(unconfiguredGameIds);
  };

  // Handle selecting games with missing ratings
  const handleSelectAllUnconfiguredRatings = async () => {
    const unconfiguredGameIds = await selectAllUnconfiguredRatings();
    setSelectedGames(unconfiguredGameIds);
  };

  // Handle retry failed games
  const handleRetryFailed = () => {
    const failedGameIds = getFailedGameIds();
    setSelectedGames(failedGameIds);
    setProgressDialogOpen(false);
    // The bulk action will be triggered again with the failed games
    setTimeout(() => {
      handleBulkAction();
    }, 100);
  };

  if (isLoading && page === 1 && !searchTerm) {
    return <AdminGamesLoadingState />;
  }

  return (
    <div className="space-y-6">
      <AdminGamesHeader 
        onAddNewGame={() => {
          setEditingGame(null);
          setEditDialogOpen(true);
        }}
        onToggleTools={() => setShowTools(!showTools)}
        showTools={showTools}
      />

      {/* Tools Section */}
      {showTools && (
        <AdminGamesTools />
      )}

      <AdminGamesFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        categories={categories}
        totalGames={totalGames}
        isLoading={isLoading}
        setPage={setPage}
      />

      <AdminGamesBulkActions
        selectedGames={selectedGames}
        bulkAction={bulkAction}
        setBulkAction={setBulkAction}
        bulkCategory={bulkCategory}
        setBulkCategory={setBulkCategory}
        bulkFakePlayCount={bulkFakePlayCount}
        setBulkFakePlayCount={setBulkFakePlayCount}
        bulkFakePlayMin={bulkFakePlayMin}
        setBulkFakePlayMin={setBulkFakePlayMin}
        bulkFakePlayMax={bulkFakePlayMax}
        setBulkFakePlayMax={setBulkFakePlayMax}
        bulkRatingsCountMin={bulkRatingsCountMin}
        setBulkRatingsCountMin={setBulkRatingsCountMin}
        bulkRatingsCountMax={bulkRatingsCountMax}
        setBulkRatingsCountMax={setBulkRatingsCountMax}
        useFakePlayRange={useFakePlayRange}
        setUseFakePlayRange={setUseFakePlayRange}
        categories={categories}
        onBulkAction={handleBulkAction}
        onClearSelection={clearBulkSelection}
        isLoading={isActionLoading}
      />

      <AdminGamesSelectHeader
        selectedGames={selectedGames}
        games={games}
        totalGames={totalGames}
        onSelectAll={handleSelectAll(games, setSelectedGames)}
        onSelectAllPages={handleSelectAllPages(setSelectedGames)}
        onSelectAllUnconfigured={async () => {
          const unconfiguredGameIds = await selectAllUnconfiguredGames();
          setSelectedGames(unconfiguredGameIds);
        }}
        onSelectAllUnconfiguredPlayCount={async () => {
          const unconfiguredGameIds = await selectAllUnconfiguredPlayCount();
          setSelectedGames(unconfiguredGameIds);
        }}
        onSelectAllUnconfiguredRatings={async () => {
          const unconfiguredGameIds = await selectAllUnconfiguredRatings();
          setSelectedGames(unconfiguredGameIds);
        }}
        onClearSelection={clearBulkSelection}
      />

      {/* Games List */}
      {isLoading ? (
        <AdminGamesLoadingState />
      ) : games.length === 0 ? (
        <AdminGamesEmptyState />
      ) : (
        <div className="space-y-1">
          {games.map((game) => (
            <AdminGameCard
              key={game.id}
              game={game}
              isSelected={selectedGames.includes(game.id)}
              onSelect={handleSelectGame(setSelectedGames)}
              onEdit={(game) => {
                setEditingGame(game);
                setEditDialogOpen(true);
              }}
              onEditFakePlayCount={(game) => {
                setEditingPlayCountGame(game);
                setFakePlayCountDialogOpen(true);
              }}
              onToggleVisibility={handleToggleVisibility}
              onDelete={handleDeleteGame}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <AdminGamesPagination
          currentPage={page}
          totalPages={pageCount}
          onPageChange={setPage}
        />
      )}

      <GameEditDialog
        game={editingGame}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />

      <FakePlayCountDialog
        game={editingPlayCountGame}
        open={fakePlayCountDialogOpen}
        onOpenChange={setFakePlayCountDialogOpen}
      />

      <BulkActionProgressDialog
        open={progressDialogOpen}
        onOpenChange={setProgressDialogOpen}
        action={progressData.action}
        totalGames={progressData.totalGames}
        processedGames={progressData.processedGames}
        results={progressData.results}
        isComplete={progressData.isComplete}
        onRetryFailed={() => {
          const failedGameIds = getFailedGameIds();
          setSelectedGames(failedGameIds);
          setProgressDialogOpen(false);
          setTimeout(() => {
            handleBulkAction();
          }, 100);
        }}
      />
    </div>
  );
};

export default AdminGames;
